import React, { useEffect, useRef, useState } from 'react';
import { adminListCollections, adminCreateCollection, adminUpdateCollection, adminDeleteCollection, adminListProducts, adminUploadImageFile } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, X, Star, Upload, Loader2 } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';

const empty = { name: '', slug: '', tagline: '', description: '', image: '', product_ids: [], featured: false, active: true, order: 0, seo_title: '', seo_description: '' };

const AdminCollections = () => {
  const { format } = useCurrency();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => adminListCollections().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); adminListProducts().then(setProducts); }, []);

  const onUploadFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setUploading(true);
    try {
      const meta = await adminUploadImageFile(file);
      const url = meta.url?.startsWith('http') ? meta.url : (process.env.REACT_APP_BACKEND_URL + meta.url);
      setEditing(prev => ({ ...prev, image: url }));
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onSave = async () => {
    try {
      if (editing.id) await adminUpdateCollection(editing.id, editing); else await adminCreateCollection(editing);
      toast.success('Collection saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const onDelete = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await adminDeleteCollection(c.id); toast.success('Deleted'); load();
  };

  const togglePid = (pid) => {
    setEditing(e => ({ ...e, product_ids: e.product_ids.includes(pid) ? e.product_ids.filter(x => x !== pid) : [...e.product_ids, pid] }));
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif-display text-4xl">Collections</h1>
          <p className="text-sm text-neutral-600 mt-1">Curated groupings for landing sections and marketing.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 bg-[#213A60] hover:bg-[#D4A63A] text-white px-5 py-2.5 rounded-full text-xs uppercase tracking-widest"><Plus className="w-4 h-4" /> New Collection</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => (
          <div key={c.id} className="bg-white rounded-xl border border-[#E7DCC7] overflow-hidden group">
            <div className="aspect-[16/9] bg-neutral-100 overflow-hidden">{c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">No image</div>}</div>
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-serif-display text-xl">{c.name}</span>
                    {c.featured && <Star className="w-3.5 h-3.5 fill-[#E8B364] stroke-[#E8B364]" />}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">/{c.slug} · {c.product_ids?.length || 0} products</div>
                </div>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${c.active ? 'bg-green-50 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}>{c.active ? 'Active' : 'Hidden'}</span>
              </div>
              <p className="text-xs text-neutral-600 mt-2 line-clamp-2">{c.tagline}</p>
              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => setEditing({ ...c, product_ids: c.product_ids || [] })} className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-[#E7DCC7] rounded-full py-2 hover:border-[#D4A63A] hover:text-[#8A6A22]"><Edit3 className="w-3 h-3" /> Edit</button>
                <button onClick={() => onDelete(c)} className="px-3 py-2 text-xs border border-[#E7DCC7] rounded-full hover:border-red-500 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <>
          <div onClick={() => setEditing(null)} className="fixed inset-0 bg-black/50 z-50" />
          <aside className="fixed top-0 right-0 h-full w-full sm:w-[600px] bg-white z-50 overflow-y-auto shadow-2xl">
            <div className="px-6 py-5 border-b border-[#E7DCC7] flex items-center justify-between">
              <h3 className="font-serif-display text-2xl">{editing.id ? 'Edit Collection' : 'New Collection'}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Name</span><input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Slug</span><input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A] font-mono" /></label>
              </div>
              <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Tagline</span><input value={editing.tagline || ''} onChange={e => setEditing({ ...editing, tagline: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
              <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Description</span><textarea rows="3" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
              <div>
                <span className="text-[11px] uppercase tracking-widest text-neutral-600">Image</span>
                <div className="mt-1 space-y-2">
                  {editing.image && (
                    <div className="relative w-full aspect-[16/9] bg-neutral-100 rounded-lg overflow-hidden border border-[#E7DCC7]">
                      <img src={editing.image} alt="preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setEditing({ ...editing, image: '' })}
                        className="absolute top-2 right-2 w-7 h-7 bg-white/95 rounded-full flex items-center justify-center hover:bg-white text-neutral-700 shadow"
                        aria-label="Clear image"
                        title="Clear image"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      value={editing.image || ''}
                      onChange={e => setEditing({ ...editing, image: e.target.value })}
                      placeholder="Paste image URL or use Upload"
                      className="flex-1 bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]"
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => onUploadFile(e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 border border-[#E7DCC7] hover:border-[#D4A63A] hover:text-[#8A6A22] rounded-full px-4 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
                      data-testid="collection-image-upload"
                    >
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                  <p className="text-[10px] text-neutral-500">JPG / PNG / WebP · resized to 1600px WebP · stored in the built-in CDN</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Order</span><input type="number" value={editing.order || 0} onChange={e => setEditing({ ...editing, order: +e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                <div className="pt-6 space-y-2">
                  <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.featured} onChange={e => setEditing({ ...editing, featured: e.target.checked })} /> Featured on homepage</label>
                  <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-600 mb-2">Products in this collection ({editing.product_ids?.length || 0})</div>
                <div className="max-h-72 overflow-y-auto border border-[#E7DCC7] rounded-xl divide-y divide-[#E7DCC7]">
                  {products.map(p => (
                    <label key={p.id} className="flex items-center gap-3 p-3 hover:bg-[#F8F4EC] cursor-pointer">
                      <input type="checkbox" checked={editing.product_ids?.includes(p.id)} onChange={() => togglePid(p.id)} />
                      <img src={p.image} alt="" className="w-9 h-9 rounded object-cover" />
                      <span className="text-sm flex-1">{p.name}</span>
                      <span className="text-xs text-neutral-500">{format(p.price)}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-5 border-t border-[#E7DCC7] flex gap-3">
              <button onClick={onSave} className="flex-1 bg-[#213A60] hover:bg-[#D4A63A] text-white py-3 rounded-full text-xs uppercase tracking-widest">Save</button>
              <button onClick={() => setEditing(null)} className="px-6 py-3 border border-[#E7DCC7] rounded-full text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default AdminCollections;
